// OneToOneChatPage.jsx
import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  serverTimestamp,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

import { FiPlus, FiX, FiSmile, FiMessageSquare } from "react-icons/fi";
import { MdImage } from "react-icons/md";
import { BsFileEarmarkCode, BsFileZip } from "react-icons/bs";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Picker from "emoji-picker-react";
import { toast } from "react-toastify";
import { UserContext } from "../context/UserContext";

const storage = getStorage();

const getTimeAgo = (timestamp) => {
  if (!timestamp) return "";
  const date = timestamp.toDate?.() || new Date(timestamp);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

const OneToOneChatPage = () => {
  const { user } = useContext(UserContext);
  const [chatUsers, setChatUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [previewFiles, setPreviewFiles] = useState([]);
  const [showChatMenu, setShowChatMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef(null);

  // -------- FETCH USER CHATS --------
  useEffect(() => {
    if (!user?.id) return;

    const q = query(
      collection(db, "chats"),
      where("members", "array-contains", user.id)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const chatsData = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const otherMemberId = data.members.find((id) => id !== user.id);
        const otherMemberInfo = data.membersInfo?.[otherMemberId] || {};
        return {
          chatId: docSnap.id,
          otherMemberId,
          otherMemberInfo,
          lastMessageText: data.lastMessageText,
          lastMessageAt: data.lastMessageAt,
        };
      });

      setChatUsers(chatsData);
    });

    return () => unsubscribe();
  }, [user]);

  // -------- FETCH MESSAGES --------
  useEffect(() => {
    if (!selectedUser?.chatId) return;

    const q = query(collection(db, "chats", selectedUser.chatId, "messages"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      msgs.sort((a, b) => a.createdAt?.toDate() - b.createdAt?.toDate());
      setMessages(msgs);
    });

    return () => unsubscribe();
  }, [selectedUser]);

  // -------- AUTO SCROLL --------
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // -------- SEND MESSAGE --------
  const sendMessage = async (
    type = "text",
    content = null,
    fileName = null
  ) => {
    if (!selectedUser?.chatId) return;

    await addDoc(collection(db, "chats", selectedUser.chatId, "messages"), {
      senderId: user.id,
      senderName: user.name || "User",
      type,
      content: content || newMessage,
      fileName: fileName || null,
      createdAt: serverTimestamp(),
    });

    // --- Update last message in the same chat ---
    const chatDocRef = doc(db, "chats", selectedUser.chatId);
    await updateDoc(chatDocRef, {
      lastMessageText: content || newMessage,
      lastMessageAt: serverTimestamp(),
    });

    setNewMessage("");
    setPreviewFiles([]);
    setShowEmojiPicker(false);
  };

  // -------- FILE UPLOAD --------
  const handleFileUpload = async (file) => {
    if (!file) return;
    const fileRef = ref(storage, `chatFiles/${Date.now()}-${file.name}`);
    await uploadBytes(fileRef, file);
    const url = await getDownloadURL(fileRef);
    const type = file.type.includes("image") ? "image" : "file";
    await sendMessage(type, url, file.name);
  };

  // -------- CODE SNIPPET --------
  const handleCodeSnippet = async () => {
    const snippet = prompt("Paste your code snippet:");
    if (snippet) await sendMessage("code", snippet);
  };

  // -------- HANDLE SEND BUTTON --------
  const handleSend = async () => {
    if (!newMessage.trim() && previewFiles.length === 0) return;

    if (newMessage.trim()) await sendMessage("text", newMessage);

    for (const item of previewFiles) {
      if (item.type === "image" || item.type === "file") {
        await handleFileUpload(item.file);
      } else if (item.type === "code") {
        await sendMessage("code", item.content);
      }
    }

    setNewMessage("");
    setPreviewFiles([]);
  };

  // -------- HANDLE FILE SELECT --------
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const previews = files.map((file) => ({
      file,
      type: file.type.startsWith("image") ? "image" : "file",
      url: URL.createObjectURL(file),
    }));
    setPreviewFiles([...previewFiles, ...previews]);
  };

  // -------- HANDLE EMOJI --------
  const handleEmojiClick = (emojiData) => {
    const emojiChar = emojiData.emoji || emojiData.native || "";
    setNewMessage((prev) => prev + emojiChar);
    setShowEmojiPicker(false);
  };

  return (
    <Container>
      {/* ---------- LEFT CHAT USERS ---------- */}
      <UserList showList={!selectedUser}>
        <Header>
          One-to-One Chats
          {selectedUser && (
            <CloseButton onClick={() => setSelectedUser(null)}>×</CloseButton>
          )}
        </Header>

        {chatUsers.map((chat) => (
          <UserItem
            key={chat.chatId}
            onClick={() =>
              setSelectedUser({
                ...chat.otherMemberInfo,
                id: chat.otherMemberId,
                chatId: chat.chatId,
              })
            }
            active={selectedUser?.id === chat.otherMemberId}
          >
            <AvatarWrapper>
              {chat.otherMemberInfo.profilePic ? (
                <img
                  src={chat.otherMemberInfo.profilePic}
                  alt="avatar"
                  style={{ width: "46px", height: "46px", borderRadius: "50%" }}
                />
              ) : (
                <Avatar>{chat.otherMemberInfo.name[0].toUpperCase()}</Avatar>
              )}
            </AvatarWrapper>
            <UserInfo>
              <UserName>{chat.otherMemberInfo.name}</UserName>
              <UserEmail>{chat.otherMemberInfo.email}</UserEmail>
              <small style={{ fontSize: "10px", color: "#aaa" }}>
                {chat.lastMessageText || "No messages yet"}
              </small>
            </UserInfo>
          </UserItem>
        ))}
      </UserList>

      {/* ---------- RIGHT CHAT WINDOW ---------- */}
      <ChatWindow showWindow={!!selectedUser}>
        {selectedUser ? (
          <>
            <ChatHeader>
              <HeaderTitle>{selectedUser.name}</HeaderTitle>
              <CloseButtonMobile onClick={() => setSelectedUser(null)}>
                <FiMessageSquare size={22} style={{ cursor: "pointer" }} />
              </CloseButtonMobile>
            </ChatHeader>

            <Messages>
              {messages.map((msg) => (
                <Message key={msg.id} isOwn={msg.senderId === user.id}>
                  <SenderName>{msg.senderName}</SenderName>
                  <MessageBubble isOwn={msg.senderId === user.id}>
                    {msg.type === "text" && msg.content}
                    {msg.type === "image" && (
                      <img
                        src={msg.content}
                        alt="chat-img"
                        style={{ maxWidth: "200px", borderRadius: "6px" }}
                      />
                    )}
                    {msg.type === "file" && (
                      <a
                        href={msg.content}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        📎 {msg.fileName}
                      </a>
                    )}
                    {msg.type === "code" && (
                      <CodeContainer>
                        <pre>
                          <code>{msg.content}</code>
                        </pre>
                      </CodeContainer>
                    )}
                  </MessageBubble>
                  <MessageTime>{getTimeAgo(msg.createdAt)}</MessageTime>
                </Message>
              ))}
              <div ref={messagesEndRef} />
            </Messages>

            {/* ---------- CHAT INPUT ---------- */}
            <ChatBox>
              <AttachmentMenu>
                {showChatMenu ? (
                  <FiX onClick={() => setShowChatMenu(false)} size={22} />
                ) : (
                  <FiPlus onClick={() => setShowChatMenu(true)} size={22} />
                )}

                {showChatMenu && (
                  <AttachmentOptions>
                    <Option>
                      <label>
                        <MdImage /> Image
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleFileSelect}
                        />
                      </label>
                    </Option>
                    <Option>
                      <label>
                        <BsFileZip /> File
                        <input type="file" hidden onChange={handleFileSelect} />
                      </label>
                    </Option>
                    <Option
                      onClick={() => {
                        handleCodeSnippet();
                        setShowChatMenu(false);
                      }}
                    >
                      <BsFileEarmarkCode /> Code
                    </Option>
                    <Option
                      onClick={() => {
                        setShowEmojiPicker((prev) => !prev);
                        setShowChatMenu(false);
                      }}
                    >
                      <FiSmile /> Emoji
                    </Option>
                  </AttachmentOptions>
                )}
              </AttachmentMenu>

              {previewFiles.length > 0 && (
                <FilePreviewContainer>
                  {previewFiles.map((item, idx) => (
                    <PreviewItem key={idx}>
                      {item.type === "image" && (
                        <img src={item.url} alt="preview" width="60" />
                      )}
                      {item.type === "file" && <span>📎 {item.file.name}</span>}
                      <FiX
                        size={16}
                        style={{
                          cursor: "pointer",
                          position: "absolute",
                          top: 2,
                          right: 2,
                        }}
                        onClick={() =>
                          setPreviewFiles((prev) =>
                            prev.filter((_, i) => i !== idx)
                          )
                        }
                      />
                    </PreviewItem>
                  ))}
                </FilePreviewContainer>
              )}

              <MessageInput
                type="text"
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
              />
              <SendButton onClick={handleSend}>Send</SendButton>

              {showEmojiPicker && (
                <EmojiWrapper>
                  <Picker onEmojiClick={handleEmojiClick} />
                </EmojiWrapper>
              )}
            </ChatBox>
          </>
        ) : (
          <EmptyState>Select a chat from the list</EmptyState>
        )}
      </ChatWindow>
    </Container>
  );
};

export default OneToOneChatPage;

/* ---------- Styled Components ---------- */
/* ---------- Improved Styled Components (Glassy Dev Chat UI) ---------- */
const UserList = styled.div`
  width: 300px;
  border-right: 1px solid rgba(155, 92, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  overflow-y: auto;
  transition: left 0.3s ease-in-out;
  position: relative;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: ${(props) => (props.showList ? "0" : "-100%")};
    height: 100%;
    z-index: 100;
    width: 100%;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(155, 92, 255, 0.2);
    border-radius: 10px;
  }
`;

const ChatWindow = styled.div`
  flex: 1;
  display: ${(props) => (props.showWindow ? "flex" : "none")};
  flex-direction: column;
  backdrop-filter: blur(8px);

  @media (min-width: 769px) {
    display: flex;
  }
`;

const Header = styled.div`
  padding: 16px;
  font-weight: 600;
  text-align: center;
  color: #fff;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  border-bottom: 1px solid rgba(155, 92, 255, 0.15);
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #aaa;
  cursor: pointer;
  font-size: 20px;
  transition: 0.2s;

  &:hover {
    color: #fff;
  }
`;

const CloseButtonMobile = styled(CloseButton)`
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
`;

const Container = styled.div`
  display: flex;
  height: 88vh;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 0 40px rgba(155, 92, 255, 0.1);
`;

const Avatar = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: linear-gradient(135deg, #9b5cff, #c59fff);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 20px;
  color: white;
  overflow: hidden;
  flex-shrink: 0;
`;

const UserItem = styled.div`
  padding: 10px;
  display: flex;
  gap: 10px;
  cursor: pointer;
  align-items: center;
  border-radius: 10px;
  margin-bottom: 6px;
  transition: 0.25s ease;
  background: ${({ active }) =>
    active
      ? "linear-gradient(90deg, rgba(155,92,255,0.3), rgba(197,159,255,0.15))"
      : "transparent"};

  &:hover {
    background: rgba(155, 92, 255, 0.15);
    transform: translateX(2px);
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const UserName = styled.span`
  font-weight: 600;
  font-size: 15px;
`;
const UserEmail = styled.span`
  font-size: 12px;
  color: #aaa;
`;

const ChatHeader = styled.div`
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(155, 92, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-weight: 600;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Messages = styled.div`
  flex: 1;
  padding: 18px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.2);

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(155, 92, 255, 0.2);
    border-radius: 10px;
  }
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ isOwn }) => (isOwn ? "flex-end" : "flex-start")};
  margin-bottom: 14px;
`;

const SenderName = styled.div`
  font-size: 11px;
  color: #999;
  margin-bottom: 4px;
`;

const MessageBubble = styled.div`
  background: ${({ isOwn }) =>
    isOwn
      ? "linear-gradient(135deg, rgba(155,92,255,0.9), rgba(197,159,255,0.8))"
      : "rgba(255,255,255,0.08)"};
  color: #fff;
  padding: 10px 14px;
  border-radius: 14px;
  max-width: 70%;
  word-wrap: break-word;
  box-shadow: ${({ isOwn }) =>
    isOwn
      ? "0 0 12px rgba(155,92,255,0.3)"
      : "0 0 10px rgba(255,255,255,0.05)"};
  transition: 0.2s;
  backdrop-filter: blur(6px);

  img {
    max-width: 200px;
    border-radius: 10px;
  }

  a {
    color: #c59fff;
    text-decoration: none;
  }
`;

const MessageTime = styled.div`
  font-size: 10px;
  color: #aaa;
  margin-top: 3px;
`;

const CodeContainer = styled.div`
  background: #0f0f0f;
  color: #c5c5c5;
  padding: 10px;
  border-radius: 10px;
  font-family: "Fira Code", monospace;
  max-width: 350px;
  overflow-x: auto;
  border: 1px solid rgba(155, 92, 255, 0.2);
`;

const ChatBox = styled.div`
  padding: 10px 16px;
  border-top: 1px solid rgba(155, 92, 255, 0.15);
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.05);
  position: relative;
`;

const AttachmentMenu = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  color: #c59fff;
`;

const AttachmentOptions = styled.div`
  position: absolute;
  bottom: 45px;
  left: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: rgba(20, 20, 20, 0.95);
  padding: 8px;
  border-radius: 10px;
  border: 1px solid rgba(155, 92, 255, 0.25);
  z-index: 10;
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #fff;
  padding: 6px 10px;
  border-radius: 8px;
  transition: 0.2s ease;

  &:hover {
    background: rgba(155, 92, 255, 0.25);
  }
`;

const FilePreviewContainer = styled.div`
  position: absolute;
  bottom: 60px;
  left: 20px;
  display: flex;
  gap: 8px;
  z-index: 20;
`;

const PreviewItem = styled.div`
  position: relative;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    border-radius: 6px;
  }
`;

const MessageInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(155, 92, 255, 0.15);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  transition: 0.25s;

  &:focus {
    outline: none;
    border: 1px solid rgba(155, 92, 255, 0.5);
    box-shadow: 0 0 10px rgba(155, 92, 255, 0.2);
  }
`;

const SendButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: #fff;
  border: none;
  padding: 10px 16px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 20px rgba(155, 92, 255, 0.3);
  }
`;

const EmojiWrapper = styled.div`
  position: absolute;
  bottom: 60px;
  right: 20px;
  z-index: 100;
  background: rgba(20, 20, 20, 0.95);
  border-radius: 10px;
  border: 1px solid rgba(155, 92, 255, 0.25);
  overflow: hidden;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #aaa;
  text-align: center;
`;
const AvatarWrapper = styled.div`
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
